/**
 * PSV-1729 — top-right leaderboard HUD widget.
 *
 * Classic .io-style scoreboard: top-5 players ranked by gems (kills as
 * tiebreak), rank 1 crowned gold, own row tinted cyan with a faint
 * underlay. When the local player falls out of the top 5, a separated
 * sixth row shows their real rank + stats — you always see yourself.
 *
 * Pure render widget: ArenaController assembles the entries (gems/kills
 * from GameState broadcasts + names from PlayerJoined) and pushes them
 * in via `setEntries` at broadcast rate — the panel holds no extra
 * subscriptions of its own. To stay cheap at that rate it keeps a fixed
 * pool of 6 rows' text objects (created once in build(), never
 * created/destroyed per update; unused rows are blanked via setText(''))
 * plus one shared Graphics for the own-row underlay and the sixth-row
 * separator line, and skips the repaint entirely when the computed
 * display strings are unchanged (cheap join compare).
 */

import Phaser from 'phaser';

const PANEL_WIDTH = 210;
const PANEL_MARGIN = 16;
const PAD_X = 12;
const PANEL_RADIUS = 8;

// Header (container-local coords).
const TITLE_Y = 8;

// Rows — top-5 band starts under the header; the pooled sixth row (own
// rank when outside the top 5) sits below it with a small gap for the
// separator line. Row positions are static (fixed pool), so the gap is
// baked into row index 5's Y.
const ROWS_TOP = 26;
const ROW_HEIGHT = 18;
const TOP_ROW_COUNT = 5;
const OWN_ROW_GAP = 4;
const POOL_SIZE = TOP_ROW_COUNT + 1;
const PAD_BOTTOM = 8;

// Column X offsets (container-local). Score cells are right-aligned
// (setOrigin(1, 0)) into fixed columns: kills against the right pad,
// gems ending just left of the kills column.
const RANK_X = PAD_X;
const NAME_X = 32;
const NAME_MAX_CHARS = 10;
const KILLS_RIGHT_X = PANEL_WIDTH - PAD_X;
const GEMS_RIGHT_X = KILLS_RIGHT_X - 36;

const PANEL_BG_COLOR = 0x0b101c;
const PANEL_BG_ALPHA = 0.92;
const PANEL_BORDER_COLOR = 0x2c3a5c;
const OWN_UNDERLAY_COLOR = 0x35c4f0;
const OWN_UNDERLAY_ALPHA = 0.08;
const SEPARATOR_COLOR = 0x2c3a5c;

const COLOR_TITLE = '#7fd6ea';
const COLOR_NAME = '#eeeeee';
const COLOR_OWN = '#35c4f0';
const COLOR_GOLD = '#ffd84a';
const COLOR_DIM = '#8a93ad';
const COLOR_KILLS = '#ff5577';

export interface LeaderboardEntry {
  userId: string;
  name: string;
  gems: number;
  kills: number;
}

/** Fixed-pool per-row text refs — built once, mutated in setEntries(). */
interface RowTexts {
  rankText: Phaser.GameObjects.Text;
  nameText: Phaser.GameObjects.Text;
  gemsText: Phaser.GameObjects.Text;
  killsText: Phaser.GameObjects.Text;
}

/** Computed display model for one visible row. */
interface RowView {
  rank: number;
  isOwn: boolean;
  name: string;
  gems: number;
  kills: number;
}

/** Container-local Y of a pooled row (index 5 carries the separator gap). */
function rowY(index: number): number {
  const gap = index >= TOP_ROW_COUNT ? OWN_ROW_GAP : 0;
  return ROWS_TOP + index * ROW_HEIGHT + gap;
}

export class LeaderboardPanel {
  private readonly scene: Phaser.Scene;
  private readonly myUserId: string;

  private container: Phaser.GameObjects.Container | null = null;
  private background: Phaser.GameObjects.Graphics | null = null;
  /** One shared Graphics for the own-row underlay + sixth-row separator. */
  private rowFx: Phaser.GameObjects.Graphics | null = null;
  private rows: RowTexts[] = [];

  /** Join of every row's display strings — repaint skipped when stable. */
  private lastSignature = '';
  /** Current painted panel height — bg repainted only when it changes. */
  private currentHeight = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene, myUserId: string) {
    this.scene = scene;
    this.myUserId = myUserId;
    this.build();
    this.setEntries([]);
  }

  /**
   * Push the full standings in (broadcast rate). Sorts DESC by gems with
   * kills as tiebreak, shows the top 5, and appends the own row below a
   * separator when the local player isn't among them.
   */
  setEntries(entries: LeaderboardEntry[]): void {
    if (this.destroyed) return;

    const sorted = [...entries].sort(
      (a, b) => b.gems - a.gems || b.kills - a.kills,
    );

    const views: RowView[] = [];
    let ownInTop = false;
    for (let i = 0; i < sorted.length && i < TOP_ROW_COUNT; i++) {
      const entry = sorted[i]!;
      const isOwn = entry.userId === this.myUserId;
      ownInTop = ownInTop || isOwn;
      views.push({
        rank: i + 1,
        isOwn,
        name: entry.name,
        gems: entry.gems,
        kills: entry.kills,
      });
    }

    // Own player below the fold → separated sixth row with the real rank.
    let hasOwnExtraRow = false;
    if (!ownInTop) {
      const ownIndex = sorted.findIndex((e) => e.userId === this.myUserId);
      if (ownIndex >= 0) {
        const own = sorted[ownIndex]!;
        hasOwnExtraRow = true;
        views.push({
          rank: ownIndex + 1,
          isOwn: true,
          name: own.name,
          gems: own.gems,
          kills: own.kills,
        });
      }
    }

    // Cheap change detection: one string per row (fields + own flag fully
    // determine the paint), joined — bail out when nothing moved.
    const signature = views
      .map((v) => `${v.rank}|${v.isOwn ? 1 : 0}|${v.name}|${v.gems}|${v.kills}`)
      .join('\n');
    if (signature === this.lastSignature) return;
    this.lastSignature = signature;

    this.applyRows(views, hasOwnExtraRow);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.container?.destroy(true);
    this.container = null;
    this.background = null;
    this.rowFx = null;
    this.rows = [];
  }

  // ---------- private ----------

  private build(): void {
    const cam = this.scene.cameras.main;
    const panelX = cam.width - PANEL_WIDTH - PANEL_MARGIN;
    const panelY = PANEL_MARGIN;

    this.container = this.scene.add
      .container(panelX, panelY)
      .setScrollFactor(0)
      .setDepth(18);

    // Background — painted by repaintBackground() whenever the row count
    // (and therefore the panel height) changes.
    this.background = this.scene.add.graphics();
    this.container.add(this.background);

    // Own-row underlay + separator line sit above the bg, below the texts.
    this.rowFx = this.scene.add.graphics();
    this.container.add(this.rowFx);

    // Header — spaced small-caps title, dim cyan (StatPanel styling).
    const title = this.scene.add.text(PAD_X, TITLE_Y, 'L E A D E R B O A R D', {
      fontSize: '11px',
      color: COLOR_TITLE,
    });
    this.container.add(title);

    // Fixed pool: 6 rows of rank/name/gems/kills texts, created once and
    // only mutated (setText/setColor) afterwards. Unused rows are blank.
    this.rows = [];
    for (let i = 0; i < POOL_SIZE; i++) {
      const y = rowY(i);

      const rankText = this.scene.add.text(RANK_X, y + 3, '', {
        fontSize: '11px',
        color: COLOR_DIM,
      });
      this.container.add(rankText);

      const nameText = this.scene.add.text(NAME_X, y + 2, '', {
        fontSize: '12px',
        color: COLOR_NAME,
      });
      this.container.add(nameText);

      const gemsText = this.scene.add
        .text(GEMS_RIGHT_X, y + 3, '', {
          fontSize: '11px',
          color: COLOR_GOLD,
        })
        .setOrigin(1, 0);
      this.container.add(gemsText);

      const killsText = this.scene.add
        .text(KILLS_RIGHT_X, y + 3, '', {
          fontSize: '11px',
          color: COLOR_KILLS,
        })
        .setOrigin(1, 0);
      this.container.add(killsText);

      this.rows.push({ rankText, nameText, gemsText, killsText });
    }
  }

  /** Write the computed rows into the pool + repaint chrome Graphics. */
  private applyRows(views: RowView[], hasOwnExtraRow: boolean): void {
    let ownRowIndex: number | null = null;

    for (let i = 0; i < POOL_SIZE; i++) {
      const row = this.rows[i]!;
      const view = views[i];

      if (view === undefined) {
        row.rankText.setText('');
        row.nameText.setText('');
        row.gemsText.setText('');
        row.killsText.setText('');
        continue;
      }

      if (view.isOwn) ownRowIndex = i;

      // Rank cell — gold crown for #1, dim number otherwise (the sixth
      // row shows the own REAL rank, e.g. 7).
      if (view.rank === 1) {
        row.rankText.setText('♔');
        row.rankText.setColor(COLOR_GOLD);
      } else {
        row.rankText.setText(String(view.rank));
        row.rankText.setColor(COLOR_DIM);
      }

      row.nameText.setText(view.name.slice(0, NAME_MAX_CHARS));
      row.nameText.setColor(view.isOwn ? COLOR_OWN : COLOR_NAME);

      row.gemsText.setText(`◆${view.gems}`);
      row.killsText.setText(`✖${view.kills}`);
    }

    // Height tracks the visible row count; the own extra row also carries
    // its separator gap.
    const height =
      ROWS_TOP +
      views.length * ROW_HEIGHT +
      (hasOwnExtraRow ? OWN_ROW_GAP : 0) +
      PAD_BOTTOM;
    if (height !== this.currentHeight) {
      this.currentHeight = height;
      this.repaintBackground();
    }

    this.repaintRowFx(ownRowIndex, hasOwnExtraRow);
  }

  /** Rounded card bg — repainted only when the panel height changes. */
  private repaintBackground(): void {
    if (this.destroyed || this.background === null) return;
    this.background.clear();
    this.background.fillStyle(PANEL_BG_COLOR, PANEL_BG_ALPHA);
    this.background.fillRoundedRect(
      0,
      0,
      PANEL_WIDTH,
      this.currentHeight,
      PANEL_RADIUS,
    );
    this.background.lineStyle(1, PANEL_BORDER_COLOR, 1);
    this.background.strokeRoundedRect(
      0,
      0,
      PANEL_WIDTH,
      this.currentHeight,
      PANEL_RADIUS,
    );
  }

  /** Faint cyan underlay on the own row + separator above the sixth row. */
  private repaintRowFx(
    ownRowIndex: number | null,
    hasOwnExtraRow: boolean,
  ): void {
    if (this.destroyed || this.rowFx === null) return;

    this.rowFx.clear();

    if (hasOwnExtraRow) {
      const sepY = rowY(TOP_ROW_COUNT) - OWN_ROW_GAP / 2;
      this.rowFx.lineStyle(1, SEPARATOR_COLOR, 0.9);
      this.rowFx.lineBetween(PAD_X, sepY, PANEL_WIDTH - PAD_X, sepY);
    }

    if (ownRowIndex !== null) {
      this.rowFx.fillStyle(OWN_UNDERLAY_COLOR, OWN_UNDERLAY_ALPHA);
      this.rowFx.fillRoundedRect(
        6,
        rowY(ownRowIndex) + 1,
        PANEL_WIDTH - 12,
        ROW_HEIGHT - 2,
        5,
      );
    }
  }
}
